import React, { useState, useEffect } from 'react';
import {
  Download,
  Users,
  Building2,
  Activity,
  FileText,
  RefreshCw,
  Clock,
  AlertCircle,
  MessageSquare,
  Zap
} from 'lucide-react';
import { adminAPI, handleApiError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { FormSelect } from '../components/FormSelect';

const SystemAdminReports = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState({
    systemStats: {},
    locationStats: [],
    userStats: {},
    recentActivity: [],
    smsCredit: null,
    n8nStatus: null
  });

  const [filters, setFilters] = useState({
    dateRange: {
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
      end: new Date().toISOString().split('T')[0]
    },
    locationId: 'all',
    reportType: 'overview'
  });

  useEffect(() => {
    loadReportData();
  }, [filters.dateRange, filters.locationId]);

  const loadReportData = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Load system-wide statistics
      const statsResponse = await adminAPI.getStats();

      // Load locations data
      const locationsResponse = await adminAPI.getLocations();

      // Load users data
      const usersResponse = await adminAPI.getUsers();

      // Load SMS credit data (static for now)
      const smsCredit = 10000;

      if (statsResponse.data.success && locationsResponse.data.success && usersResponse.data.success) {
        setReportData({
          systemStats: statsResponse.data.data,
          locationStats: locationsResponse.data.data || [],
          userStats: {
            total: usersResponse.data.data.length,
            systemAdmins: usersResponse.data.data.filter(u => u.user_type === 'system_admin').length,
            patrons: usersResponse.data.data.filter(u => u.user_type === 'patron').length,
            staff: usersResponse.data.data.filter(u => u.user_type === 'staff').length,
            active: usersResponse.data.data.filter(u => u.status === 'active').length,
            inactive: usersResponse.data.data.filter(u => u.status !== 'active').length
          },
          recentActivity: [], // This would come from a dedicated endpoint
          smsCredit: smsCredit,
          n8nStatus: {
            active: true, // Would come from actual N8N status check
            lastUpdate: new Date().toISOString(),
            workflow: 'SMS Credit Monitor'
          }
        });
      }
    } catch (err) {
      console.error('Failed to load report data:', err);
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const exportReport = () => {
    // Create CSV data
    const csvData = [
      ['Sistem Raporu', ''],
      ['Tarih:', new Date().toLocaleDateString('tr-TR')],
      ['Dönem:', `${filters.dateRange.start} - ${filters.dateRange.end}`],
      [''],
      ['Genel İstatistikler', ''],
      ['Toplam Lokasyon', reportData.systemStats.totalLocations || 0],
      ['Aktif Lokasyon', reportData.systemStats.activeLocations || 0],
      ['Toplam Kullanıcı', reportData.userStats.total || 0],
      ['Aktif Kullanıcı', reportData.userStats.active || 0],
      ['SMS Kredisi', reportData.smsCredit || 'N/A'],
      [''],
      ['Kullanıcı Tipleri', ''],
      ['Sistem Yöneticisi', reportData.userStats.systemAdmins || 0],
      ['İşletme Sahibi', reportData.userStats.patrons || 0],
      ['Personel', reportData.userStats.staff || 0],
      [''],
      ['Lokasyon Detayları', ''],
      ['Lokasyon Adı', 'Tip', 'Durum', 'Kullanıcı Sayısı'],
      ...reportData.locationStats.map(loc => [
        loc.name,
        loc.type || 'N/A',
        loc.is_active ? 'Aktif' : 'Pasif',
        loc.user_count || 0
      ])
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `sistem-raporu-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadReportData}
            className="btn-secondary flex items-center"
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Yenile
          </button>
          <button
            onClick={exportReport}
            className="btn-primary flex items-center"
          >
            <Download className="w-4 h-4 mr-2" />
            Rapor İndir
          </button>
        </div>
      </div>

      {/* Report filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Başlangıç Tarihi
              </label>
              <input
                type="date"
                className="form-input"
                value={filters.dateRange.start}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  dateRange: { ...prev.dateRange, start: e.target.value }
                }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bitiş Tarihi
              </label>
              <input
                type="date"
                className="form-input"
                value={filters.dateRange.end}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  dateRange: { ...prev.dateRange, end: e.target.value }
                }))}
              />
            </div>
            <div>
              <FormSelect
                name="locationId"
                label="Lokasyon"
                value={filters.locationId}
                onChange={(e) => setFilters(prev => ({ ...prev, locationId: e.target.value }))}
                options={[
                  { value: 'all', label: 'Tüm Lokasyonlar' },
                  ...reportData.locationStats.map(location => ({
                    value: location.id.toString(),
                    label: location.name
                  }))
                ]}
              />
            </div>
            <div>
              <FormSelect
                name="reportType"
                label="Rapor Tipi"
                value={filters.reportType}
                onChange={(e) => setFilters(prev => ({ ...prev, reportType: e.target.value }))}
                options={[
                  { value: 'overview', label: 'Genel Bakış' },
                  { value: 'users', label: 'Kullanıcı Analizi' },
                  { value: 'locations', label: 'Lokasyon Analizi' },
                  { value: 'activity', label: 'Aktivite Raporu' }
                ]}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="card">
          <div className="card-body text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-4">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {reportData.systemStats.totalLocations || 0}
            </div>
            <div className="text-sm text-gray-600">Toplam Lokasyon</div>
            <div className="mt-2">
              <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                {reportData.systemStats.activeLocations || 0} aktif
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mb-4">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {reportData.userStats.total || 0}
            </div>
            <div className="text-sm text-gray-600">Toplam Kullanıcı</div>
            <div className="mt-2">
              <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                {reportData.userStats.active || 0} aktif
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mb-4">
              <Activity className="w-6 h-6 text-purple-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {reportData.userStats.patrons || 0}
            </div>
            <div className="text-sm text-gray-600">İşletme Sahibi</div>
            <div className="mt-2">
              <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                Patron
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-orange-100 rounded-lg mb-4">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {reportData.userStats.staff || 0}
            </div>
            <div className="text-sm text-gray-600">Personel</div>
            <div className="mt-2">
              <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                Staff
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-lg mb-4">
              <MessageSquare className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {reportData.smsCredit?.toLocaleString() || 'N/A'}
            </div>
            <div className="text-sm text-gray-600">SMS Kredisi</div>
            <div className="mt-2">
              <span className={`text-xs px-2 py-1 rounded-full ${
                reportData.smsCredit > 1000
                  ? 'bg-green-100 text-green-600'
                  : reportData.smsCredit > 0
                    ? 'bg-yellow-100 text-yellow-600'
                    : 'bg-gray-100 text-gray-600'
              }`}>
                {reportData.smsCredit > 1000 ? 'Yeterli' : reportData.smsCredit > 0 ? 'Düşük' : 'Bilinmiyor'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* N8N Monitoring Status */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Zap className="w-5 h-5 mr-2" />
            N8N Otomasyon Durumu
          </h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg mb-2">
                <Activity className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-sm font-medium text-gray-900">Workflow Durumu</div>
              <div className="text-xs text-gray-600 mt-1">
                <span className="px-2 py-1 bg-green-100 text-green-600 rounded-full">
                  {reportData.n8nStatus?.active ? 'Aktif' : 'Pasif'}
                </span>
              </div>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg mb-2">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div className="text-sm font-medium text-gray-900">Son Güncelleme</div>
              <div className="text-xs text-gray-600 mt-1">
                {reportData.n8nStatus?.lastUpdate ?
                  new Date(reportData.n8nStatus.lastUpdate).toLocaleString('tr-TR') : 'Bilinmiyor'
                }
              </div>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg mb-2">
                <MessageSquare className="w-5 h-5 text-purple-600" />
              </div>
              <div className="text-sm font-medium text-gray-900">SMS Monitoring</div>
              <div className="text-xs text-gray-600 mt-1">
                <span className="px-2 py-1 bg-blue-100 text-blue-600 rounded-full">
                  5 dakikada bir
                </span>
              </div>
            </div>
          </div>
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">N8N Workflow:</span>
              <span className="text-sm font-medium text-gray-900">{reportData.n8nStatus?.workflow || 'SMS Credit Monitor'}</span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-sm text-gray-700">URL:</span>
              <a href="http://localhost:5678" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800">
                http://localhost:5678
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Reports Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Distribution */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Kullanıcı Dağılımı
            </h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-purple-500 rounded-full mr-3"></div>
                  <span className="text-sm text-gray-700">Sistem Yöneticisi</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {reportData.userStats.systemAdmins || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-blue-500 rounded-full mr-3"></div>
                  <span className="text-sm text-gray-700">İşletme Sahibi</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {reportData.userStats.patrons || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-sm text-gray-700">Personel</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {reportData.userStats.staff || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* SMS Credit Details */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <MessageSquare className="w-5 h-5 mr-2" />
              SMS Kredi Detayları
            </h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-blue-500 rounded-full mr-3"></div>
                  <span className="text-sm text-gray-700">Mevcut Kredi</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {reportData.smsCredit?.toLocaleString() || 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-4 h-4 rounded-full mr-3 ${
                    reportData.smsCredit > 1000 ? 'bg-green-500' : 'bg-yellow-500'
                  }`}></div>
                  <span className="text-sm text-gray-700">Durum</span>
                </div>
                <span className={`text-sm font-medium ${
                  reportData.smsCredit > 1000 ? 'text-green-600' : 'text-yellow-600'
                }`}>
                  {reportData.smsCredit > 1000 ? 'Yeterli' : 'Dikkat Gerekli'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-purple-500 rounded-full mr-3"></div>
                  <span className="text-sm text-gray-700">Uyarı Eşiği</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  1,000 SMS
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-orange-500 rounded-full mr-3"></div>
                  <span className="text-sm text-gray-700">Kontrol Sıklığı</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  5 dakika
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default SystemAdminReports;